import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCanManagePatientFinancials } from '@features/billing/permissions/useCanManagePatientFinancials';
import type { PaymentSheetRequest } from '@features/patient-ledger';
import { useOpenLedgerDrawer, useOpenPaymentSheet } from '@features/patient-ledger';
import { useStripeConnect, useStripeDashboard } from '@features/payments';
import { useSubjectLanguageVariants } from '@features/user';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, Button, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { FeatureGates, useFeatureGate } from '@services/statsig';

import type { PatientQueueItem, SentInvoiceItem } from '../../../store';
import { usePatientBillingQueueQuery, usePatientBillingSentQuery } from '../../../store';
import { PatientResponsibilityPanel } from '../../PatientResponsibilityPanel';
import {
  type ColumnDef,
  DataTable,
  formatCents,
  formatDeliveryMethod,
  Pagination,
  SegmentControl,
  TableToolbar,
  useColumnPreferences,
} from '../shared';

const getQueueColumns = (singularCap: string): ColumnDef[] => [
  { key: 'patient_name', label: singularCap, width: '1.2fr', type: 'patient', required: true },
  {
    key: 'balance',
    label: 'Balance',
    width: '100px',
    type: 'mono',
    align: 'right',
    required: true,
  },
  { key: 'from', label: 'From', width: '1.2fr' },
  { key: 'queued_at', label: 'Queued', width: '100px' },
  { key: 'why', label: 'Why', width: '2fr', type: 'action', required: true },
  { key: 'action', label: '', width: '100px', type: 'actionButton' },
];

const getSentColumns = (singularCap: string): ColumnDef[] => [
  { key: 'patient_name', label: singularCap, width: '1.2fr', type: 'patient', required: true },
  { key: 'amount', label: 'Amount', width: '90px', type: 'mono', align: 'right', required: true },
  { key: 'status', label: 'Status', width: '80px', type: 'chip', required: true },
  { key: 'created_at', label: 'Sent', width: '140px' },
  { key: 'delivery_method', label: 'Method', width: '120px' },
  { key: 'outcome', label: 'Outcome', width: '1.5fr', type: 'action' },
];

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Map sent invoice status to chip color keys
const statusToChipKey: Record<string, string> = {
  paid: 'clean',
  open: 'inFlight',
  void: 'imported',
  uncollectible: 'denials',
};

type ReminderState = 'NEW' | 'REMINDER_DUE' | 'MULTIPLE_REMINDERS';

interface ReminderBadgeInfo {
  state: ReminderState;
  label: string;
  color: 'default' | 'warning' | 'error';
}

function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return null;
  const diffMs = Date.now() - ts;
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
}

function getReminderBadge(item: PatientQueueItem): ReminderBadgeInfo {
  if (!item.latest_invoice_id) {
    return { state: 'NEW', label: 'New charge', color: 'default' };
  }
  const days = daysSince(item.latest_action_at);
  const daysLabel = days != null ? `${days}d ago` : 'recent';
  const count = item.reminder_count ?? 0;
  if (count === 0) {
    return {
      state: 'REMINDER_DUE',
      label: `Reminder due (${daysLabel})`,
      color: 'warning',
    };
  }
  return {
    state: 'MULTIPLE_REMINDERS',
    label: `Reminder ${count} · ${daysLabel}`,
    color: 'error',
  };
}

export const PatientBillingTab: React.FC = () => {
  const canManagePatientFinancials = useCanManagePatientFinancials();
  const { singularCap } = useSubjectLanguageVariants();
  const QUEUE_COLUMNS = useMemo(() => getQueueColumns(singularCap), [singularCap]);
  const SENT_COLUMNS = useMemo(() => getSentColumns(singularCap), [singularCap]);

  const [subTab, setSubTab] = useState<'queue' | 'sent'>('queue');
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [selectedPatientName, setSelectedPatientName] = useState<string | null>(null);
  const [queuePage, setQueuePage] = useState(1);
  const [queuePerPage, setQueuePerPage] = useState(50);
  const [sentPage, setSentPage] = useState(1);
  const [sentPerPage, setSentPerPage] = useState(50);
  const openLedgerDrawer = useOpenLedgerDrawer();
  const openPaymentSheet = useOpenPaymentSheet();
  // gdn-2957: money actions open the page sheet for gated users; ungated
  // users keep the drawer, still their only surface.
  const showBalanceTable = useFeatureGate(FeatureGates.PATIENT_BALANCE_TABLE);

  const openMoneyAction = useCallback(
    (request: PaymentSheetRequest) => {
      // Belt to the row verbs' braces (gdn-3287): the queue's collect
      // actions are withheld below, and this makes the helper itself
      // inert if a future call site forgets.
      if (!canManagePatientFinancials) return;
      if (showBalanceTable) {
        openPaymentSheet(request);
        return;
      }
      openLedgerDrawer({
        patientId: request.patientId,
        patientName: request.patientName,
        encounterId: request.encounterId ?? undefined,
        defaultAmountCents: request.defaultAmountCents,
        chargeLineIds: request.chargeLineIds,
        initialTab: 'ledger',
        initialAction: request.action,
        source: request.source,
      });
    },
    // canManagePatientFinancials belongs here: useCan resolves from the
    // user-info query, so it is false on the first render and flips true
    // when that lands. Omit it and the memoised closure keeps the false —
    // a permitted user's verbs would silently do nothing, forever.
    [showBalanceTable, openPaymentSheet, openLedgerDrawer, canManagePatientFinancials],
  );

  const navigate = useNavigate();

  // Stripe Connect status — drives the banner / dashboard link above the table
  const { hasStripeAccount, isStripeEnabled, isLoading: stripeLoading } = useStripeConnect();
  const { openDashboard, isLoading: isDashboardLoading } = useStripeDashboard();

  const {
    allColumns: queueAllCols,
    visibleKeys: queueVisKeys,
    visibleColumns: queueVisCols,
    updatePreferences: updateQueuePrefs,
  } = useColumnPreferences('patient-billing-queue', QUEUE_COLUMNS);

  /**
   * Drop the queue's action COLUMN for a view-only user (gdn-3287).
   *
   * Withholding onAction is not enough and neither is the guard inside
   * openMoneyAction: DataTable renders an `actionButton` cell whenever the
   * column is visible and calls `onAction?.(row)`, so both leave a button
   * that looks live and silently does nothing on click. A dead control is
   * worse than an absent one — nothing tells the biller why the click did
   * not work. The column is the only thing that governs the button.
   */
  const queueColumns = useMemo(
    () =>
      canManagePatientFinancials
        ? queueVisCols
        : queueVisCols.filter(col => col.type !== 'actionButton'),
    [queueVisCols, canManagePatientFinancials],
  );
  const {
    allColumns: sentAllCols,
    visibleKeys: sentVisKeys,
    visibleColumns: sentVisCols,
    updatePreferences: updateSentPrefs,
  } = useColumnPreferences('patient-billing-sent', SENT_COLUMNS);

  const {
    data: queueData,
    isLoading: queueLoading,
    isFetching: queueFetching,
  } = usePatientBillingQueueQuery({
    page: queuePage,
    perPage: queuePerPage,
  });
  const {
    data: sentData,
    isLoading: sentLoading,
    isFetching: sentFetching,
  } = usePatientBillingSentQuery({
    page: sentPage,
    perPage: sentPerPage,
  });
  const segments = useMemo(
    () => [
      { key: 'queue', label: 'Queue', count: queueData?.summary?.count ?? 0, severity: null },
      { key: 'sent', label: 'Sent', count: sentData?.summary?.count ?? 0, severity: null },
    ],
    [queueData, sentData],
  );

  const queueRows = useMemo(() => {
    if (!queueData?.items) return [];
    return queueData.items.map((item: PatientQueueItem) => {
      const badge = getReminderBadge(item);
      return {
        id: item.patient_billing_account_id,
        patient_id: item.patient_id,
        patient_name: item.patient_name ?? '—',
        balance: formatCents(item.balance_cents),
        from: item.date_range
          ? `${item.encounter_count} encounter${item.encounter_count !== 1 ? 's' : ''} · ${item.date_range}`
          : `${item.encounter_count} encounter${item.encounter_count !== 1 ? 's' : ''}`,
        queued_at: formatDate(item.queued_at),
        why: item.why,
        action: badge.state === 'NEW' ? 'Send' : 'Remind',
        _raw: item,
        _badge: badge,
      };
    });
  }, [queueData]);

  const sentRows = useMemo(() => {
    if (!sentData?.items) return [];
    return sentData.items.map((item: SentInvoiceItem) => ({
      id: item.invoice_id,
      patient_id: item.patient_id,
      patient_name: item.patient_name ?? '—',
      amount: formatCents(item.amount_cents),
      status: statusToChipKey[item.status] ?? item.status,
      status_label: item.status.charAt(0).toUpperCase() + item.status.slice(1),
      created_at: formatDate(item.created_at),
      delivery_method: formatDeliveryMethod(item.delivery_method),
      outcome: item.outcome,
    }));
  }, [sentData]);

  const queueSummary = useMemo(() => {
    if (!queueData?.summary) return '';
    const s = queueData.summary;
    return `${s.count} patient${s.count !== 1 ? 's' : ''} owe ${formatCents(s.total_cents)}${s.oldest_days ? ` · oldest: ${s.oldest_days} days` : ''}`;
  }, [queueData]);

  const sentSummary = useMemo(() => {
    if (!sentData?.summary) return '';
    const s = sentData.summary;
    const parts = [`${s.count} invoices sent`, formatCents(s.total_cents) + ' total'];
    if (s.paid_count) parts.push(`${s.paid_count} paid (${formatCents(s.paid_total_cents)})`);
    if (s.failed_count) parts.push(`${s.failed_count} failed`);
    return parts.join(' · ');
  }, [sentData]);

  const handleRowClick = useCallback((row: Record<string, unknown>) => {
    const patientId = row.patient_id as string | undefined;
    if (!patientId) return;
    setSelectedPatientId(patientId);
    setSelectedPatientName(row.patient_name as string | null);
  }, []);

  const handleSendAction = useCallback(
    (row: Record<string, unknown>) => {
      const raw = row._raw as PatientQueueItem;
      // Reminder rows: re-send the existing Stripe invoice via the dedicated
      // /remind endpoint instead of creating a brand-new invoice.
      if (raw.latest_invoice_id) {
        openMoneyAction({
          patientId: raw.patient_id,
          patientName: raw.patient_name,
          defaultAmountCents: raw.balance_cents,
          action: { kind: 'settle', method: 'remind_invoice' },
          source: 'jotbill_patient_billing_reminder',
        });
        return;
      }
      // Open the settle surface with the sheet pre-opened to invoice.
      // No defaultAmountCents: the composer preselects the
      // patient's invoiceable charge lines, which already carry the balance —
      // prefilling it would mint a duplicate ad-hoc charge (double bill).
      openMoneyAction({
        patientId: raw.patient_id,
        patientName: raw.patient_name,
        action: { kind: 'settle', method: 'invoice' },
        source: 'jotbill_patient_billing',
      });
    },
    [openMoneyAction],
  );

  // If a patient is selected, show the detail view
  if (selectedPatientId) {
    return (
      <PatientResponsibilityPanel
        patientId={selectedPatientId}
        patientName={selectedPatientName}
        // Without this the panel defaults to true and slice 1's gating is
        // defeated on the JotBill route entirely (gdn-3287) — that gating
        // only ever reached the patient-page route, where BillingSection
        // supplies the capability.
        canManagePatientFinancials={canManagePatientFinancials}
        onBack={() => {
          setSelectedPatientId(null);
          setSelectedPatientName(null);
        }}
      />
    );
  }

  const isLoading = subTab === 'queue' ? queueLoading : sentLoading;
  if (isLoading && !(subTab === 'queue' ? queueData : sentData)) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Skeleton variant="rounded" sx={{ height: 42, width: 300 }} />
        <Skeleton variant="rounded" sx={{ height: 400 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Stripe Connect status — prompt to set up, or provide a dashboard link */}
      {!stripeLoading && !hasStripeAccount && (
        <Alert
          severity="info"
          action={
            <Button
              size="small"
              variant="contained"
              onClick={() => navigate('/practice/billing-settings?tab=patient-payments')}
            >
              Set Up
            </Button>
          }
        >
          Connect your Stripe account to start collecting patient payments, sending invoices, and
          saving payment methods on file.
        </Alert>
      )}
      {!stripeLoading && hasStripeAccount && !isStripeEnabled && (
        <Alert
          severity="warning"
          action={
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => navigate('/practice/billing-settings?tab=patient-payments')}
            >
              Finish Setup
            </Button>
          }
        >
          Your Stripe Connect setup is incomplete. Finish onboarding to start collecting payments.
        </Alert>
      )}
      {!stripeLoading && isStripeEnabled && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            variant="text"
            endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            onClick={() => openDashboard()}
            disabled={isDashboardLoading}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            {isDashboardLoading ? 'Opening…' : 'Open Stripe Dashboard'}
          </Button>
        </Box>
      )}

      <SegmentControl
        segments={segments}
        active={subTab}
        onChange={key => setSubTab(key as 'queue' | 'sent')}
      />

      {subTab === 'queue' ? (
        <>
          <TableToolbar
            summary={queueSummary}
            allColumns={queueAllCols}
            visibleKeys={queueVisKeys}
            onColumnsChange={updateQueuePrefs}
            paginationData={queueData}
            onPageChange={setQueuePage}
          />
          <Box data-testid="patient-billing-table-scroll" sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 800 }}>
              <DataTable
                columns={queueColumns}
                rows={queueRows}
                onRowClick={handleRowClick}
                onAction={canManagePatientFinancials ? handleSendAction : undefined}
                isFetching={queueFetching}
                renderCell={(col, row) => {
                  if (col.key !== 'why') return null;
                  const badge = row._badge as ReminderBadgeInfo | undefined;
                  if (!badge) return null;
                  return (
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
                      <Chip
                        label={badge.label}
                        size="small"
                        color={badge.color}
                        variant={badge.color === 'default' ? 'outlined' : 'filled'}
                        sx={{ height: 20, fontSize: '0.625rem', fontWeight: 600 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {String(row.why ?? '')}
                      </Typography>
                    </Stack>
                  );
                }}
              />
            </Box>
          </Box>
          {queueData && (
            <Pagination
              data={queueData}
              onPageChange={setQueuePage}
              onPerPageChange={n => {
                setQueuePerPage(n);
                setQueuePage(1);
              }}
            />
          )}
        </>
      ) : (
        <>
          <TableToolbar
            summary={sentSummary}
            allColumns={sentAllCols}
            visibleKeys={sentVisKeys}
            onColumnsChange={updateSentPrefs}
            paginationData={sentData}
            onPageChange={setSentPage}
          />
          <Box data-testid="patient-billing-table-scroll" sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 800 }}>
              <DataTable
                columns={sentVisCols}
                rows={sentRows}
                onRowClick={handleRowClick}
                isFetching={sentFetching}
              />
            </Box>
          </Box>
          {sentData && (
            <Pagination
              data={sentData}
              onPageChange={setSentPage}
              onPerPageChange={n => {
                setSentPerPage(n);
                setSentPage(1);
              }}
            />
          )}
        </>
      )}
    </Box>
  );
};
